import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { FILTER_TYPE } from '../../models/filter-type';
import { KeyValue } from '../../models/key-value';
import { SelectAutocompleteService } from '../../services/select-autocomplete.service';
/**
 * The Autocomplete component.
 *
 * This autocomplete has 4 types of filter:
 *  By_value, by_key and by_value_or_code are managed by the component itself but the custom is managed by the parent component.
 * Also, when the filter is custom, a paginator option is available( set values to totalResults and pageSize)
 */
@Component({
    selector: 'cw-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [
        SelectAutocompleteService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class AutocompleteComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * The input data.
     * If the filter type is not custom, this data is used to filter.
     * If the filter type is custom, this data has to be filtered by the parent component
     */
    @Input() dataList: KeyValue[] = [];
    /** It specifies a short hint that describes the expected value of the component */
    @Input() placeholder = '';
    /** indicates the minimum value for the search to start. By default is 3*/
    @Input() minValueLength = 3;
    /** indicates the filter type. By default is filter by value*/
    @Input() filterType = FILTER_TYPE.BY_VALUE;
    /** ngModel of selectedOption */
    @Input() selectedOption: KeyValue | null = null;
    /** This param is only neccessary when the filter is custom and the paginator has to be active */
    @Input() totalResults = 0;
    /**
     * This param is only neccessary when the filter is custom and the paginator has to be active.
     * Indicates the number of elements by page
     */
    @Input() pageSize = 10;
    /** Shows a little description  below the input tag*/
    private _selectedOptionInfo = '';
    @Input()
    set selectedOptionInfo(value: string) {
        const hadValue = !!this._selectedOptionInfo;
        this._selectedOptionInfo = value;

        if (hadValue && !value && this.searchText) {
            this.resetSearchText();
        }
    }

    get selectedOptionInfo(): string {
        return this._selectedOptionInfo;
    }
    /** This param is only neccessary when the filter is custom and you get the data asynchronously */
    @Input() loading = false;
    /**
     * If the filter type is custom, this parameter indicates the time in milliseconds that the user
     * must be without writing to emit the event searchTextChange
     */
    @Input() delayCustomSearch = 250;
    /**
     * Displays the menu with position absolute over the rest of the content
     */
    @Input() floating = false;
    /**
     * Boolean to display the selectedOptionInfo htnml block
     *
     */
    @Input() hiddenOptionInfo = false;
    /** This param will emit value when the filter is custom
     * and the search has more than or equal number of characters than minValueLength param
     */
    @Output() searchTextChange = new EventEmitter<string>();
    /** This param will emit value when a option is selected in the list */
    @Output() selectedOptionChange = new EventEmitter<KeyValue | null>();
    /** This param will emit value when the paginator is active,
     * the filter is custom and the page selected is not equal to the current page
     */
    @Output() currentPageChange = new EventEmitter<number>();

    optionList: KeyValue[] = [];
    searchText = '';
    notFound = false;
    currentPage = 1;
    filterTypeEnum = FILTER_TYPE;
    maxItemPerPage = 10;

    private searchTextSubject: Subject<string> = new Subject<string>();
    private searchTextSubscription: Subscription = new Subscription();
    // These methods are required because we are using Control Value Accessor
    /* eslint-disable */
    private onChange = (value: KeyValue | null) => {};
    private onTouch = () => {};
    /* eslint-enable */

    constructor(
        private autocompleteService: SelectAutocompleteService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.optionList = this.dataList;
        this.searchTextSubscription = this.searchTextSubject
            .pipe(debounceTime(this.delayCustomSearch), distinctUntilChanged())
            .subscribe((value) => {
                this.searchTextChange.emit(value);
            });
    }

    ngOnChanges(): void {
        if (this.filterType === FILTER_TYPE.CUSTOM) {
            const notResultsFound =
                !this.dataList.length && this.searchText.length >= this.minValueLength && !this.loading && !this.selectedOptionInfo.length;
            this.updateOptionList(this.dataList, notResultsFound);
        }
    }

    ngOnDestroy(): void {
        this.searchTextSubscription?.unsubscribe();
    }

    /** @ignore */
    writeValue(data: KeyValue | null): void {
        if (!data) {
            return;
        }

        this.onUpdateSelectedOption(data);
    }

    /** @ignore */
    registerOnChange(fn: (value: KeyValue | null) => void): void {
        this.onChange = fn;
    }

    /** @ignore */
    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    /** @ignore */
    onUpdateSelectedOption(option: KeyValue): void {
        if (this.searchText.length < this.minValueLength && !option.key && !option.value) {
            return;
        }
        this.searchText = option.value;
        this.selectedOptionChange.emit(option);
        this.onChange(option);
        this.resetList();
        // [INTEGRATION] To solve some refreshing problems when using the autocomplete inside pointcloudviz ui.
        this.cdr.detectChanges();
    }

    /** @ignore */
    isHiddenMenu(): boolean {
        return !!this.selectedOptionInfo.length || this.searchText.length < this.minValueLength;
    }

    /** @ignore */
    filterOptions(inputValue: string): void {
        this.searchText = inputValue;
        if (inputValue.length < this.minValueLength) {
            this.resetSelectedOption();
            this.resetList();
            return;
        }

        if (this.filterType === FILTER_TYPE.CUSTOM) {
            this.searchTextSubject.next(this.searchText);
        } else {
            const result = this.autocompleteService.filterOptions(this.dataList, this.filterType, this.searchText);
            this.updateOptionList(result, !result.length);
        }
    }

    /** @ignore */
    changePage(newPage: number): void {
        if (this.filterType === FILTER_TYPE.CUSTOM && newPage !== this.currentPage) {
            this.currentPageChange.emit(newPage);
            this.currentPage = newPage;
        }
    }

    /**
     * Reset the searchText property
     * @param checkMenuOpened If true reset it only if menu is opened
     */
    resetSearchText(): void {
        this.searchText = '';
    }

    /**
     * Handles the click event on the document to close the list
     * @param event click event
     * @param parentElement The parent element of the autocomplete component
     */
    onDocumentClick(event: MouseEvent, parentElement: HTMLElement): void {
        const target = event.target as HTMLElement;
        const isParent = parentElement === target;
        const isDescendant = parentElement.contains(target as HTMLElement);
        const hasAutocompleteClass = target.closest('[class*="autocomplete"]');

        if (isParent || isDescendant || hasAutocompleteClass || this.isHiddenMenu()) {
            return;
        }

        this.resetSearchText();
    }

    get menuClassStyles(): Record<string, boolean> {
        return { active: this.searchText.length >= this.minValueLength, hide: this.isHiddenMenu(), 'twc-select-menu': this.floating };
    }

    private updateOptionList(list: KeyValue[], notFound: boolean): void {
        this.optionList = list;
        this.notFound = notFound;

        if (this.selectedOption && this.notFound) {
            this.resetSearchText();
            this.resetSelectedOption();
        }
    }

    private resetList(): void {
        this.optionList = [];
        this.notFound = false;
    }

    private resetSelectedOption(): void {
        this.onChange(null);
    }

    get found(): boolean {
        return !this.notFound;
    }
}
